/**
 * Applications domain API functions.
 *
 * Backend: /api/admin/applications (org-scoped, admin/manager only).
 * Documents are never exposed publicly — presigned URLs are fetched on demand.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  parseAdminErrorResponse,
} from "./core";

export function fetchAdminApplications({ status, q, limit, offset } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const qs = params.toString();
  const url = `${API_BASE_URL}/api/admin/applications${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * GET /api/admin/applications/picker-options — ``{items, total}`` (C3).
 *
 * A dedicated route rather than a widened ``/applications``: that one's
 * response_model is a bare list with no total, and several admin pages
 * consume it as an array. Items carry the same ``ApplicationListItem``
 * shape, ordered live-first (new → in review → accepted → converted →
 * rejected/archived), newest inside each bucket.
 */
export function fetchAdminApplicationsPage({ q, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/applications/picker-options${
    qs ? `?${qs}` : ""
  }`;
  return fetch(url, { headers: getApiHeaders() }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    const data = await res.json();
    if (!data || !Array.isArray(data.items)) {
      throw new Error(
        'Ungültige Antwort von GET /api/admin/applications/picker-options: erwartet paginierte Antwort mit "items".'
      );
    }
    return {
      items: data.items,
      total: typeof data.total === "number" ? data.total : data.items.length,
    };
  });
}

export function fetchAdminApplication(applicationId) {
  return fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminApplication(applicationId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export async function updateAdminApplication(applicationId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}`,
    {
      method: "PUT",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export function fetchAdminApplicationDocumentDownloadUrl(
  applicationId,
  documentId
) {
  return fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(
      applicationId
    )}/documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export function fetchAdminApplicationNotes(applicationId) {
  return fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}/notes`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function createAdminApplicationNote(applicationId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}/notes`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/applications/{id}/convert
 *
 * Konvertiert eine angenommene Bewerbung in einen Mieter. Kein Request-Body.
 * Response: { application: {...}, tenant: {...} } (Combined-Response).
 * Wirft bei 404 (unbekannt / soft-deleted) und 409 (bereits konvertiert /
 * nicht-angenommen) — Fehlertext via parseAdminErrorResponse.
 */
export async function convertAdminApplication(applicationId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/applications/${encodeURIComponent(applicationId)}/convert`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}
